import { ChangeDetectorRef, Component, computed, inject, Injector, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
   LcsAdvancedMultiSelectDatasource,
   LcsAdvancedMultiSelectOpenDialogEvent,
   LcsAdvancedMultiSelectReadonlyStateType,
   LcsAdvancedMultiSelectStateEventPayloads,
   LcsAdvancedMultiSelectStateInitEventPayload,
   LcsAdvancedMultiSelectTriggerConfig,
   LcsAdvancedMultiSelectTriggerFormGroup,
} from '@lcs/shared/advanced-multi-select';
import { LcsSelectWithAllDisplayTypes } from '@lcs/shared/select-with-all';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ExtensionGroupOption } from 'src/app/common-components/entity-select/extension-group/models/extension-group-option';
import { extensionGroupOptionActions } from 'src/app/common-components/entity-select/extension-group/state/extension-group-option.actions';
import { extensionGroupOptionSelectors } from 'src/app/common-components/entity-select/extension-group/state/extension-group-option.reducer';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { ReportParameterComponent } from '../../report-parameter-list/report-parameter-component';
import { ReportParameterConfig } from '../../report-parameter-list/report-parameter.config';
import { ExtensionReportParameterTabs } from './extensions-report-parameter.component';

@Component({
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: ExtensionGroupsReportParameterComponent }],
    selector: 'ndt-extension-groups-report-parameter',
    templateUrl: './extension-groups-report-parameter.component.html',
    styleUrls: ['./extension-group-report-parameter.component.scss'],
    standalone: false
})
export class ExtensionGroupsReportParameterComponent implements OnDestroy, ReportParameterComponent {
   form: UntypedFormGroup;
   selectedAccountId: number | null;
   searchControl = new UntypedFormControl('');

   private destroyed$ = new Subject<void>();

   config: ReportParameterConfig;

   groupEntityName = 'Extension Groups';

   readonly store = inject(Store);
   readonly cd = inject(ChangeDetectorRef);
   readonly injector = inject(Injector);

   readonly allGroups = this.store.selectSignal(extensionGroupOptionSelectors.selectAll);
   readonly groups = this.store.selectSignal(extensionGroupOptionSelectors.selectFilteredItems);
   readonly groupsCallState = this.store.selectSignal(extensionGroupOptionSelectors.selectCallState);

   readonly groupItemsFromControl = computed(
      () => {
         const groupItemsControl = this.form.controls.extensionGroups;
         return groupItemsControl
            ? toSignal<ExtensionGroupOption[]>(
                 groupItemsControl.valueChanges.pipe(map((v) => groupItemsControl.getRawValue() ?? [])),
                 {
                    initialValue: groupItemsControl?.getRawValue() ?? [],
                    injector: this.injector,
                 }
              )()
            : signal<ExtensionGroupOption[]>([])();
      },
      { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
   );

   readonly allFromControl = computed(() => {
      const allControl = this.form.controls.all;
      return allControl
         ? toSignal(allControl.valueChanges.pipe(map((v) => allControl.getRawValue() ?? false)), {
              initialValue: allControl?.getRawValue() ?? false,
              injector: this.injector,
           })()
         : signal(false)();
   });

   readonly triggerConfig = computed<LcsAdvancedMultiSelectTriggerConfig<ExtensionGroupOption>>(() => {
      return {
         displayOptions: {
            label: this.groupEntityName,
            displayType: LcsSelectWithAllDisplayTypes.Label,
            displayWithFn: (
               items: ExtensionGroupOption[],
               state: LcsAdvancedMultiSelectReadonlyStateType<ExtensionGroupOption>
            ) => {
               const options = this.groupItemsFromControl() ?? [];
               if (options.length === 1) {
                  return `${options[0].name}`;
               }
               const entityName = this.groupEntityName;
               return `${options.length} ${entityName}`;
            },
            hasAllCheckbox: true,
         },
         group: new LcsAdvancedMultiSelectTriggerFormGroup<ExtensionGroupOption>(
            {
               items: new FormControl(this.groupItemsFromControl() ?? [], { nonNullable: true }),
               all: new FormControl(this.form.controls.all.value, { nonNullable: true }),
            },
            { validators: [] }
         ),
         syncOn: 'save',
      };
   });

   readonly selectedGroups = computed(() => {
      return signal(this.groupItemsFromControl());
   });

   readonly datasources = computed(() => {
      return [this.groupDataSource()];
   });

   readonly groupDataSource = computed<LcsAdvancedMultiSelectDatasource<ExtensionGroupOption>>(() => {
      return {
         id: ExtensionReportParameterTabs.Group,
         label: this.groupEntityName,
         items: this.availableGroups() ?? [],
         selectedItems: this.selectedGroups()(),
         itemTrackBy: this.groupTrackBy,
         displayOptions: {
            displayWithFn: (item: ExtensionGroupOption) => {
               return item.name;
            },
         },
         callState: this.groupsCallState(),
      };
   });

   readonly availableGroups = computed(() => {
      const groups = this.groups();
      return groups;
   });

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onInit(e: LcsAdvancedMultiSelectStateInitEventPayload<ExtensionGroupOption>) {
      this.store
         .select(getSelectedAccountId)
         .pipe(takeUntil(this.destroyed$))
         .subscribe((selectedAccountId) => {
            if (selectedAccountId) {
               this.selectedAccountId = selectedAccountId;
               this.form.controls.accountId.setValue(this.selectedAccountId);
               this.store.dispatch(
                  extensionGroupOptionActions.load({
                     additionalFields: [],
                     filterOverrides: [`accountId = ${selectedAccountId}`],
                  })
               );
            }
         });
   }

   onOpen(e: LcsAdvancedMultiSelectOpenDialogEvent<ExtensionGroupOption>) {
      this.store.dispatch(extensionGroupOptionActions.searchReset());
   }

   onSave(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionGroupOption>['save']) {
      const selectedItems = e.state.selectedItems();
      this.form.controls.extensionGroups.setValue(selectedItems);
   }

   onSearch(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionGroupOption>['search']) {
      this.store.dispatch(extensionGroupOptionActions.search({ searchTerm: e.searchText }));
   }

   onAllChange(all: boolean) {
      this.form.controls.all.setValue(all);
   }

   onSelectedItemsChange(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionGroupOption>['selectedItemsChange']) {
      this.selectedGroups().set(e.state.selectedItems() as ExtensionGroupOption[]);
   }

   onClose(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionGroupOption>['close']) {
      this.selectedGroups().set(this.groupItemsFromControl());
   }

   private groupTrackBy = (group: ExtensionGroupOption) => group.extensionGroupId;
}
